import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";

// Type definitions for the optimized client data
export interface OptimizedClient {
  // Client fields
  id: string;
  name: string | null;
  tax_id: string | null;
  email: string | null;
  phone_number: string | null;
  assigned_seller: string | null;
  created_by: string;
  created_at: string;
  insured_amount: number | null;
  trailing_payment_days: number | null;
  risk_checked: boolean | null;
  sales_condition_id: string | null;
  credit_used_amount: number | null;
  is_test: boolean | null;
  aux_category: string | null;
  fiscal_address_id: string | null;
  activity: string | null;
  activity_code: string | null;
  internal_notes: string | null;
  deleted_at: string | null;
  sales_request_pending_amount: number | null;
  manual_facturas_pending_amount: number | null;
  contact_person: string | null;
  is_active: boolean | null;
  deactivation_reason: string | null;

  // Related data
  seller_name: string | null;
  seller_email: string | null;
  sales_condition_name: string | null;
  sales_condition_type: string | null;
  fiscal_street: string | null;
  fiscal_number: string | null;
  fiscal_city: string | null;
  fiscal_comuna: string | null;
  fiscal_state: string | null;
  collection_contact_person: string | null;
  collection_contact_email: string | null;
  collection_contact_phone: string | null;
  sales_channel: string | null;
  auxiliary_type: string | null;
  sales_channel_name: string | null;
  auxiliary_type_name: string | null;
}

interface UseOptimizedClientsParams {
  sellerFilter?: string;
  searchQuery?: string;
  paymentHistoryFilter?: string;
  activeFilter?: string; // active, inactive, all
  limit?: number;
  offset?: number;
}

export const useOptimizedClients = ({
  sellerFilter = "all",
  searchQuery = "",
  paymentHistoryFilter = "all",
  activeFilter = "all",
  limit = 100,
  offset = 0,
}: UseOptimizedClientsParams = {}) => {
  const { id: userId, role: userRole } = useProfileStore();
  const [clients, setClients] = useState<OptimizedClient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchClients = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // For now, use the fallback approach while we debug the function signature
      await fetchClientsWithFallback();
    } catch (err) {
      console.error("Error fetching optimized clients:", err);
      setError("No se pudieron cargar los clientes");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsWithFallback = async () => {
    // Build the base query with client-side filtering as fallback
    let query = supabase
      .from("clients")
      .select(
        `
        *,
        profiles:assigned_seller(name, lastname, email),
        sales_conditions:sales_condition_id(name, type),
        addresses:fiscal_address_id(street, number, city, comuna, state),
        sales_channels:sales_channel(id, name),
        auxiliary_types:auxiliary_type(id, name)
      `
      )
      .is("deleted_at", null);

    // Apply seller filter
    if (userRole === "seller") {
      query = query.eq("assigned_seller", userId);
    } else if (sellerFilter !== "all") {
      query = query.eq("assigned_seller", sellerFilter);
    }

    // Apply active/inactive filter
    if (activeFilter === "active") {
      query = query.eq("is_active", true);
    } else if (activeFilter === "inactive") {
      query = query.eq("is_active", false);
    }

    // Apply search query
    if (searchQuery) {
      query = query.or(
        `name.ilike.%${searchQuery}%,tax_id.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,phone_number.ilike.%${searchQuery}%`
      );
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Transform the data to match OptimizedClient interface
    const transformedData: OptimizedClient[] = (data || []).map((client) => ({
      id: client.id,
      name: client.name,
      tax_id: client.tax_id,
      email: client.email,
      phone_number: client.phone_number,
      assigned_seller: client.assigned_seller,
      created_by: client.created_by,
      created_at: client.created_at,
      insured_amount: client.insured_amount,
      trailing_payment_days: client.trailing_payment_days,
      risk_checked: client.risk_checked,
      sales_condition_id: client.sales_condition_id,
      credit_used_amount: client.credit_used_amount,
      is_test: client.is_test,
      aux_category: client.aux_category,
      fiscal_address_id: client.fiscal_address_id,
      activity: client.activity,
      activity_code: client.activity_code,
      internal_notes: client.internal_notes,
      deleted_at: client.deleted_at,
      sales_request_pending_amount: null,
      manual_facturas_pending_amount: null,
      contact_person: client.contact_person,
      is_active: client.is_active,
      deactivation_reason: client.deactivation_reason,
      seller_name: client.profiles
        ? `${client.profiles.name || ""} ${client.profiles.lastname || ""}`.trim()
        : null,
      seller_email: client.profiles?.email || null,
      sales_condition_name: client.sales_conditions?.name || null,
      sales_condition_type: client.sales_conditions?.type || null,
      fiscal_street: client.addresses?.street || null,
      fiscal_number: client.addresses?.number || null,
      fiscal_city: client.addresses?.city || null,
      fiscal_comuna: client.addresses?.comuna || null,
      fiscal_state: client.addresses?.state || null,
      collection_contact_person: client.collection_contact_person,
      collection_contact_email: client.collection_contact_email,
      collection_contact_phone: client.collection_contact_phone,
      sales_channel: client.sales_channel,
      auxiliary_type: client.auxiliary_type,
      sales_channel_name: (client as any).sales_channels?.name || null,
      auxiliary_type_name: (client as any).auxiliary_types?.name || null,
    }));

    setClients(transformedData);
  };

  const fetchTotalCount = async () => {
    try {
      // For now, use the fallback approach while we debug the function signature
      await fetchTotalCountWithFallback();
    } catch (err) {
      console.error("Error fetching client count:", err);
    }
  };

  const fetchTotalCountWithFallback = async () => {
    // Build the same query but just for count
    let countQuery = supabase
      .from("clients")
      .select("id", { count: "exact", head: true })
      .is("deleted_at", null);

    // Apply seller filter
    if (userRole === "seller") {
      countQuery = countQuery.eq("assigned_seller", userId);
    } else if (sellerFilter !== "all") {
      countQuery = countQuery.eq("assigned_seller", sellerFilter);
    }

    // Apply active/inactive filter
    if (activeFilter === "active") {
      countQuery = countQuery.eq("is_active", true);
    } else if (activeFilter === "inactive") {
      countQuery = countQuery.eq("is_active", false);
    }

    // Apply search query
    if (searchQuery) {
      countQuery = countQuery.or(
        `name.ilike.%${searchQuery}%,tax_id.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,phone_number.ilike.%${searchQuery}%`
      );
    }

    const { count, error } = await countQuery;

    if (error) {
      throw error;
    }

    setTotalCount(count || 0);
  };

  useEffect(() => {
    if (userId) {
      fetchClients();
      fetchTotalCount();
    }
  }, [
    userId,
    sellerFilter,
    searchQuery,
    paymentHistoryFilter,
    activeFilter,
    limit,
    offset,
  ]);

  const refetch = () => {
    fetchClients();
    fetchTotalCount();
  };

  return {
    clients,
    isLoading,
    error,
    totalCount,
    refetch,
  };
};

// Transform function to convert optimized client to the existing Client interface
export const transformOptimizedClient = (optimizedClient: OptimizedClient) => {
  return {
    id: optimizedClient.id,
    name: optimizedClient.name || "",
    company: optimizedClient.name || "",
    email: optimizedClient.email || "",
    phone: optimizedClient.phone_number || "",
    address: "",
    creditScore: Math.random() * 10, // Random credit score for visualization
    riskScore: Math.random() * 10,
    paymentHistory: ["good", "average", "poor"][
      Math.floor(Math.random() * 3)
    ] as "good" | "average" | "poor",
    isActive: optimizedClient.is_active ?? true,
    is_active: optimizedClient.is_active ?? true,
    deactivation_reason: optimizedClient.deactivation_reason || null,
    sellerName: optimizedClient.seller_name || "No asignado",
    sellerId: optimizedClient.assigned_seller || "",
    assigned_seller: optimizedClient.assigned_seller || "",
    taxId: optimizedClient.tax_id || "",
    insuredAmount: optimizedClient.insured_amount || 0,
    trailingPaymentDays: optimizedClient.trailing_payment_days || 0,
    risk_checked: optimizedClient.risk_checked || false,
    salesConditionId: optimizedClient.sales_condition_id || "",
    salesConditionName:
      optimizedClient.sales_condition_name || "No especificada",
    contact_person: optimizedClient.contact_person || "",
    fiscalStreet: optimizedClient.fiscal_street || "",
    fiscalComuna: optimizedClient.fiscal_comuna || "",
    fiscalRegion: optimizedClient.fiscal_state || "",
    internal_notes: optimizedClient.internal_notes || "",
    deleted_at: optimizedClient.deleted_at || null,
    collection_contact_person: optimizedClient.collection_contact_person || "",
    collection_contact_email: optimizedClient.collection_contact_email || "",
    collection_contact_phone: optimizedClient.collection_contact_phone || "",
    sales_channel: optimizedClient.sales_channel || "",
    auxiliary_type: optimizedClient.auxiliary_type || "",
    sales_channel_name: optimizedClient.sales_channel_name || "",
    auxiliary_type_name: optimizedClient.auxiliary_type_name || "",
  };
};
